import React, { useRef } from "react";
import { X, Upload, File } from "lucide-react";

interface FileUploadProps {
  uploadText: string;
  uploadedFiles: File[] | null;
  onFileChange: (files: File[] | null) => void;
  fileFormat?: string;
  multiple?: boolean;
}

interface SingleFileUploadProps extends Omit<FileUploadProps, "multiple" | "uploadedFiles" | "onFileChange"> {
  uploadedFile: File | null;
  onFileChange: (file: File | null) => void;
}

interface MultiFileUploadProps extends Omit<FileUploadProps, "multiple"> {}

const FileUpload: React.FC<FileUploadProps> = ({
  uploadText,
  uploadedFiles,
  onFileChange,
  fileFormat,
  multiple = false
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const removeFile = (fileToRemove: File) => {
    const updatedFiles = uploadedFiles?.filter((file) => file.name !== fileToRemove.name) || [];
    onFileChange(updatedFiles.length > 0 ? updatedFiles : null);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);

    if (multiple) {
      const newFilesWithoutDuplicates = newFiles.filter(
        (newFile) =>
          !uploadedFiles?.some(
            (existingFile) => existingFile.name === newFile.name && existingFile.size === newFile.size
          )
      );
      if (newFilesWithoutDuplicates.length > 0) {
        onFileChange([...(uploadedFiles || []), ...newFilesWithoutDuplicates]);
      }
    } else {
      onFileChange(newFiles.length > 0 ? [newFiles[0]] : null);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="w-full space-y-3">
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-700 bg-white border border-primary-300 rounded-lg hover:bg-primary-50 hover:border-primary-400 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
      >
        <Upload className="w-4 h-4" />
        {uploadText}
      </button>

      {uploadedFiles && uploadedFiles.length > 0 && (
        <div className="space-y-2">
          {uploadedFiles.map((file) => (
            <div
              key={`${file.name}-${file.size}`}
              className="flex items-center justify-between px-4 py-3 bg-secondary-50 border border-secondary-200 rounded-lg hover:bg-secondary-100 transition-colors duration-200"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <File className="w-5 h-5 text-secondary-500 flex-shrink-0" />
                <span className="text-sm font-medium text-secondary-900 truncate" title={file.name}>
                  {file.name}
                </span>
                <span className="text-xs text-secondary-500 flex-shrink-0">({(file.size / 1024).toFixed(1)} KB)</span>
              </div>
              <button
                type="button"
                onClick={() => removeFile(file)}
                className="ml-3 flex-shrink-0 w-8 h-8 flex items-center justify-center text-error-600 bg-error-50 hover:bg-error-100 rounded-lg border border-error-200 hover:border-error-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-error-500 focus:ring-offset-2 group"
                aria-label={`Remove ${file.name}`}
              >
                <X className="w-4 h-4 group-hover:scale-110 transition-transform duration-200" />
              </button>
            </div>
          ))}
        </div>
      )}

      <input
        ref={fileInputRef}
        className="hidden"
        type="file"
        accept={fileFormat}
        onChange={handleFileSelect}
        id="file-upload-input"
        multiple={multiple}
      />
    </div>
  );
};

const SingleFileUpload: React.FC<SingleFileUploadProps> = ({ uploadText, uploadedFile, onFileChange, fileFormat }) => {
  return (
    <FileUpload
      uploadText={uploadText}
      uploadedFiles={uploadedFile ? [uploadedFile] : null}
      onFileChange={(files) => onFileChange(files ? files[0] : null)}
      fileFormat={fileFormat}
    />
  );
};

const MultiFileUpload: React.FC<MultiFileUploadProps> = ({ uploadText, uploadedFiles, onFileChange, fileFormat }) => {
  return (
    <FileUpload
      uploadText={uploadText}
      uploadedFiles={uploadedFiles}
      onFileChange={onFileChange}
      fileFormat={fileFormat}
      multiple
    />
  );
};

export { SingleFileUpload, MultiFileUpload };
